import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { motion } from 'motion/react';
import { http } from '@/network/http';
import { ADMIN_COLORS } from '@/features/admin/utils/adminAnimations';
import { getActionColor, getActionIcon, formatDateTime } from '@/features/admin/utils/adminHelpers';
import type { AuditLogEntry } from '@/features/admin/utils/adminHelpers';
import type { WidgetProps } from '../widgetRegistry';

export function AdminRecentActivityWidget({ height }: WidgetProps) {
  const { t, i18n } = useTranslation();
  const compact = height < 200;

  const { data: recentLogs, isLoading } = useQuery<{ data: AuditLogEntry[] }>({
    queryKey: ['audit-logs-dashboard'],
    queryFn: async () => {
      const { data } = await http.get('/audit', { params: { page: 1, limit: 10 } });
      return data;
    },
  });

  const recentActivity = recentLogs?.data ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-5 w-5 animate-spin" style={{ color: ADMIN_COLORS.textTertiary }} />
      </div>
    );
  }

  if (recentActivity.length === 0) {
    return (
      <p className="text-center py-8 text-sm" style={{ color: ADMIN_COLORS.textTertiary }}>
        {t('admin.noActivityData')}
      </p>
    );
  }

  return (
    <div className={compact ? 'overflow-y-auto max-h-full' : ''}>
      <div className={compact ? 'space-y-1' : 'space-y-2'}>
        {recentActivity.map((log, index) => (
          <motion.div
            key={log.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.03 }}
            className={`flex items-start gap-3 rounded-xl ${compact ? 'p-2' : 'p-3'}`}
            style={{
              backgroundColor: ADMIN_COLORS.ice,
              border: `1px solid ${ADMIN_COLORS.border}`,
            }}
          >
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: getActionColor(log.action) + '10' }}
            >
              {getActionIcon(log.action)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs" style={{ color: ADMIN_COLORS.navy }}>
                <span className="font-medium">
                  {log.userName || log.userEmail || t('audit.system')}
                </span>{' '}
                <span style={{ color: ADMIN_COLORS.textSecondary }}>
                  {t(`audit.actions.${log.action}`, log.action)}
                </span>
              </p>
              {!compact && log.entityType && (
                <p className="text-[10px] mt-0.5" style={{ color: ADMIN_COLORS.textTertiary }}>
                  {t(`audit.entityTypes.${log.entityType}`)}
                </p>
              )}
              <p className="text-[10px] mt-0.5" style={{ color: ADMIN_COLORS.textTertiary }}>
                {formatDateTime(log.createdAt, i18n.language)}
              </p>
            </div>
            <span
              className="px-2 py-0.5 rounded text-[10px] font-medium flex-shrink-0"
              style={{
                backgroundColor: getActionColor(log.action) + '10',
                color: getActionColor(log.action),
              }}
            >
              {t(`audit.actions.${log.action}`, log.action)}
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
